/**
 * Path matching for pages that must render zero Freestar scripts, slots and products.
 *
 * Patterns are matched segment by segment against a pathname:
 * - a `*` token matches exactly one segment
 * - a `**` token matches zero or more segments, and is only meaningful as the last token
 *
 * The single-segment wildcard is what keeps the lists precise: the pattern for the v3
 * dashboard matches `/players/jane-doe/v3` and everything under it, while the public
 * profile at `/players/jane-doe` — which keeps its ads — does not match.
 *
 * Each app owns its own pattern list (`AD_FREE_PAGES` in its constants file); the route
 * trees have nothing in common, so there is no shared list.
 */

function toSegments(value: string): string[] {
	const [withoutQuery = ''] = value.split('?');
	const [pathOnly = ''] = withoutQuery.split('#');

	return pathOnly.split('/').filter(Boolean);
}

function matchesPattern(segments: string[], pattern: string[]): boolean {
	let index = 0;

	for (let position = 0; position < pattern.length; position++) {
		const token = pattern[position];

		// `**` swallows whatever is left, including nothing at all, but only as the final token.
		if (token === '**') return position === pattern.length - 1;

		if (index >= segments.length) return false;
		if (token !== '*' && token !== segments[index]) return false;

		index++;
	}

	return index === segments.length;
}

export function isAdFreePath(pathname: string | null | undefined, patterns: readonly string[]): boolean {
	if (!pathname) return false;

	const segments = toSegments(pathname);

	return patterns.some((pattern) => matchesPattern(segments, toSegments(pattern)));
}
